import { AccountPlan, CoreApplicationContext } from './teamAccountModels';

export enum TeamRole {
  Admin = 0,
  Editor = 1,
  Member = 2,
  Reader = 3
}

/** Read-only roles (capped at view access): Member and Reader. Mirrors backend RolePolicy. */
export function isReadOnlyRole(role: TeamRole): boolean {
  return role === TeamRole.Member || role === TeamRole.Reader;
}

/** Roles with write access to the team's setup (pipeline, dashboards, connectors): Admin and Editor. */
export function canEdit(role: TeamRole): boolean {
  return role === TeamRole.Admin || role === TeamRole.Editor;
}

/** Roles that can create shareable view links: everyone except Reader. */
export function canShare(role: TeamRole): boolean {
  return role !== TeamRole.Reader;
}

/** Roles that can perform team-admin operations, including editing view permissions. Mirrors backend RolePolicy.CanAdminister. */
export function canAdminister(role: TeamRole): boolean {
  return role === TeamRole.Admin;
}

export interface UserContext {
  userId: string;
  emailAddress: string;
  activeAccountId: string;
  activeTeamId: string;
  teamRole: TeamRole;
  activeAccountPlan: AccountPlan;
}

export interface SessionIdentity {
  userContext: UserContext;
}

/**
 * The user context an ops app (custom dashboard view) session runs under — the ops app
 * counterpart of CoreApplicationContext.userContext, narrowed to what view code needs for
 * attribution and role-based affordances. Mirrors OpsAppUserContext in api/endpoints/core/Models.cs.
 * The MFE receives it from its token exchange; a same-origin host derives it from the
 * CoreApplicationContext it already holds via toOpsAppUserContext.
 */
export interface OpsAppUserContext {
  id: string;
  /** Currently the user's email address (the platform has no separate display name). */
  displayName: string;
  role: TeamRole;
}

export function toOpsAppUserContext(userContext: UserContext): OpsAppUserContext {
  return {
    id: userContext.userId,
    displayName: userContext.emailAddress,
    role: userContext.teamRole
  };
}

const accountPlansEqual = (a: AccountPlan, b: AccountPlan): boolean => {
  if (a.productId !== b.productId) return false;
  if (a.addOns.length !== b.addOns.length) return false;
  return a.addOns.every((addOn, i) =>
    addOn.productId === b.addOns[i].productId && addOn.quantity === b.addOns[i].quantity
  );
};

const userContextsEqual = (a: UserContext, b: UserContext): boolean => {
  return a.userId === b.userId &&
    a.emailAddress === b.emailAddress &&
    a.activeAccountId === b.activeAccountId &&
    a.activeTeamId === b.activeTeamId &&
    a.teamRole === b.teamRole &&
    accountPlansEqual(a.activeAccountPlan, b.activeAccountPlan);
}

export const sessionIdentitiesEqual = (a: SessionIdentity | null, b: SessionIdentity | null): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return userContextsEqual(a.userContext, b.userContext);
};

export function getSessionIdentity(coreApplicationContext: CoreApplicationContext | null): SessionIdentity | null {
  if (coreApplicationContext === null) {
    return null;
  }

  return {
    userContext: coreApplicationContext.userContext
  };
}
